import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Separator } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import { useI18n } from '@change-corgi/core/react/i18n';

import type { RoundShareIconButtonChannel, ShareChannelButtonConfig } from 'src/app/shared/components/share';
import { RoundShareIconButton, ShareChannelButton } from 'src/app/shared/components/share';

import { PetitionShareSlidePetitionCard } from './components/PetitionShareSlideUpHeader';
import type { PetitionShareSlideUpProps } from './types';

// These channels open an educational modal — the slide-up must close first to avoid stacked modals
const CHANNELS_WITH_EDUCATION_MODAL: readonly RoundShareIconButtonChannel[] = [
	'instagram',
	'instagramStories',
	'tiktok',
];

export const PetitionShareSlideUp: React.FC<PetitionShareSlideUpProps> = ({
	trigger,
	petition,
	buttonConfigs,
	onClose = () => {},
	continueButtonProps,
	shareVisual,
	closeOnSocialShare = true,
}) => {
	const { translate } = useI18n();
	const specialButtonConfigs: ShareChannelButtonConfig[] = [];
	const regularButtonConfigs = buttonConfigs.filter((config) => {
		if (config.channel === 'copy') {
			specialButtonConfigs.push(config);
			return false;
		}
		return true;
	});

	return (
		<Modal
			trigger={trigger}
			transformToSheet
			onClose={onClose}
			closeButtonAriaLabel={translate('corgi.components.modal.close_button')}
			className="z-100"
		>
			<ModalHeader title={shareVisual ? 'Share petition' : undefined} />

			{shareVisual && (
				<Separator className="border-stroke-secondary -mx-5 mt-4 mb-0 w-[calc(100%+2.5rem)] md:-mx-6 md:w-[calc(100%+3rem)]" />
			)}

			<ModalBody
				className={cn('max-h-[calc(85dvh-80px)] pb-6', { 'pt-0': shareVisual })}
				data-testid="petition-share-slide-up-body"
			>
				{({ close }) => (
					<div className="w-full">
						{shareVisual ? (
							<img src={shareVisual} alt="Share Visual Preview" className="mx-auto my-4 block max-w-61 rounded-lg" />
						) : (
							<div className="mb-4">
								<PetitionShareSlidePetitionCard petition={petition} />
							</div>
						)}
						{specialButtonConfigs.length > 0 && (
							<>
								<div
									className="grid grid-cols-1 gap-x-0.5 gap-y-2"
									data-testid="petition-share-slide-up-special-buttons"
								>
									{specialButtonConfigs.map((buttonConfig) => (
										<ShareChannelButton key={buttonConfig.channel} buttonConfig={buttonConfig} />
									))}
								</div>
								<Separator className="border-stroke-secondary my-5" />
							</>
						)}
						<div className="grid grid-cols-4 gap-x-0.5 gap-y-5">
							{regularButtonConfigs.map((buttonConfig) => (
								<RoundShareIconButton
									key={buttonConfig.channel}
									buttonConfig={buttonConfig}
									onBeforeShare={
										!closeOnSocialShare && !CHANNELS_WITH_EDUCATION_MODAL.includes(buttonConfig.channel)
											? undefined
											: close
									}
								/>
							))}
						</div>
					</div>
				)}
			</ModalBody>
			{continueButtonProps && (
				<ModalFooter className="justify-center" data-testid="petition-share-slide-up-footer" displayFooterTopSeparator>
					<Button variant="tertiary" className="w-full" onClick={continueButtonProps.action}>
						{continueButtonProps.label}
					</Button>
				</ModalFooter>
			)}
		</Modal>
	);
};
